import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Compass, Plus, ArrowRight, Lock, FileText, Users2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const STATUS_BADGE = {
    draft: { label: "Draft", className: "bg-slate-100 text-slate-700 border-slate-200" },
    complete: { label: "Complete", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function LrfLandingPage() {
    const [mine, setMine] = useState([]);
    const [shared, setShared] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [period, setPeriod] = useState("");
    const [creating, setCreating] = useState(false);
    const navigate = useNavigate();

    async function load() {
        try {
            setLoading(true);
            const [m, s] = await Promise.all([
                api.get("/lrf/reflections/mine"),
                api.get("/lrf/reflections/shared-with-me"),
            ]);
            setMine(m.data);
            setShared(s.data);
        } catch (e) {
            toast.error(extractError(e, "Could not load reflections"));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    async function handleCreate() {
        const label = period.trim();
        if (!label) {
            toast.error("Please enter a period label");
            return;
        }
        try {
            setCreating(true);
            const { data } = await api.post("/lrf/reflections", { period_label: label });
            toast.success("Reflection created");
            navigate(`/app/lrf/reflections/${data.id}`);
        } catch (e) {
            toast.error(extractError(e, "Could not create reflection"));
        } finally {
            setCreating(false);
        }
    }

    return (
        <div className="space-y-8" data-testid="lrf-landing">
            <div className="relative overflow-hidden rounded-3xl p-8 sm:p-10" style={{ background: "linear-gradient(135deg, #0B2545 0%, #1E56A0 60%, #8AC4FF 100%)" }}>
                <div className="absolute inset-0 pointer-events-none opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
                <div className="relative">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-[#8AC4FF] font-bold">ESSS Framework Portfolio</div>
                    <motion.h1
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-white font-semibold leading-tight max-w-3xl"
                        data-testid="lrf-title"
                    >
                        Leadership Reflection Framework
                    </motion.h1>
                    <p className="mt-4 text-[#D6E6F2] max-w-2xl leading-relaxed">
                        A structured self-reflection artifact for leaders — 6 domains, 36 objectives, 216 questions. Private by default, shareable when you're ready.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-full bg-white text-[#0B2545] hover:bg-[#D6E6F2]" data-testid="new-lrf-btn">
                                    <Plus size={14} /> Start New Reflection
                                </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="new-lrf-dialog">
                                <DialogHeader>
                                    <DialogTitle>New reflection</DialogTitle>
                                    <DialogDescription>
                                        Pick a period label (e.g., "Q1 2025", "Fall 2024"). You can rename it later.
                                    </DialogDescription>
                                </DialogHeader>
                                <Input
                                    value={period}
                                    onChange={(e) => setPeriod(e.target.value)}
                                    placeholder="Q1 2025"
                                    data-testid="new-lrf-period"
                                    autoFocus
                                />
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                                    <Button onClick={handleCreate} disabled={creating} className="bg-[#0B2545] text-white hover:bg-[#1E56A0]" data-testid="new-lrf-submit">
                                        {creating ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Create
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <Link
                            to="/app/lrf/framework"
                            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 text-white text-sm px-4 py-2 hover:bg-white/20 transition-colors"
                            data-testid="explore-framework-btn"
                        >
                            <Compass size={14} /> Explore Framework
                        </Link>
                    </div>
                </div>
            </div>

            <ReflectionList
                title="My Reflections"
                testid="my-reflections-list"
                empty="No reflections yet. Start one to begin."
                rows={mine}
                loading={loading}
                render={(r) => (
                    <Link
                        key={r.id}
                        to={`/app/lrf/reflections/${r.id}`}
                        className="block rounded-2xl border border-[#D6E6F2] bg-white p-4 hover:shadow-md transition-shadow"
                        data-testid={`my-reflection-${r.id}`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="font-serif text-lg font-semibold text-[#0B2545]">
                                    {r.period_label}
                                </div>
                                <div className="text-xs text-[#0E1B2C]/60 mt-1">
                                    Started {fmtDate(r.started_at)} · Updated {fmtDate(r.updated_at)}
                                </div>
                            </div>
                            <Badge className={(STATUS_BADGE[r.status]?.className || "") + " border"} variant="outline">
                                {r.status === "complete" && <Lock size={11} className="mr-1" />}
                                {STATUS_BADGE[r.status]?.label || r.status}
                            </Badge>
                        </div>
                        <div className="mt-3 text-[#1E56A0] text-xs font-semibold inline-flex items-center gap-1">
                            Open reflection <ArrowRight size={12} />
                        </div>
                    </Link>
                )}
            />

            <ReflectionList
                title="Shared With Me"
                testid="shared-reflections-list"
                empty="Nothing shared with you yet."
                rows={shared}
                loading={loading}
                render={(r) => (
                    <Link
                        key={r.id}
                        to={`/app/lrf/reflections/${r.id}`}
                        className="block rounded-2xl border border-[#D6E6F2] bg-white p-4 hover:shadow-md transition-shadow"
                        data-testid={`shared-reflection-${r.id}`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="font-serif text-lg font-semibold text-[#0B2545]">
                                    {r.period_label}
                                </div>
                                <div className="text-xs text-[#0E1B2C]/60 mt-1 inline-flex items-center gap-1">
                                    <Users2 size={11} /> {r.owner?.full_name || r.owner?.email || "Owner"}
                                </div>
                            </div>
                            <Badge className={(STATUS_BADGE[r.status]?.className || "") + " border"} variant="outline">
                                {STATUS_BADGE[r.status]?.label || r.status}
                            </Badge>
                        </div>
                    </Link>
                )}
            />
        </div>
    );
}

function ReflectionList({ title, testid, empty, rows, loading, render }) {
    return (
        <section data-testid={testid}>
            <h2 className="font-serif text-2xl text-[#0B2545] font-semibold">{title}</h2>
            {loading ? (
                <div className="mt-4 text-[#0B2545] inline-flex items-center gap-2 text-sm">
                    <Loader2 className="animate-spin" size={14} /> Loading…
                </div>
            ) : rows.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-[#8AC4FF] bg-[#F1F6FB] p-8 text-center text-sm text-[#0E1B2C]/70">
                    {empty}
                </div>
            ) : (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {rows.map((r) => render(r))}
                </div>
            )}
        </section>
    );
}
